import { startOfDay, endOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

export interface DayBoundsUtc {
  start: number;
  end: number;
}

/**
 * Convert a day in a site's timezone to UTC epoch bounds for the activities API.
 *
 * This is deliberately strict. An Invalid Date is truthy and `.getTime()` on one
 * yields NaN, which the API used to interpret as "no date filter" -- turning a
 * single day view into an unbounded scan of every announcement for the site.
 * Failing here is cheap; the unbounded query was not.
 */
export function toDayBoundsUtc(
  selectedDate: Date,
  timezone: string,
): DayBoundsUtc {
  if (!timezone) {
    throw new Error(
      'Cannot build activity date bounds: site timezone is missing',
    );
  }

  if (!(selectedDate instanceof Date) || Number.isNaN(selectedDate.getTime())) {
    throw new Error(
      `Cannot build activity date bounds: invalid date (${String(selectedDate)})`,
    );
  }

  const start = zonedTimeToUtc(startOfDay(selectedDate), timezone);
  const end = zonedTimeToUtc(endOfDay(selectedDate), timezone);

  // An unknown IANA zone yields an Invalid Date rather than throwing.
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error(
      `Cannot build activity date bounds: invalid timezone (${timezone})`,
    );
  }

  return { start: start.getTime(), end: end.getTime() };
}

/**
 * Non-throwing guard for react-query `enabled` flags, where a truthiness check
 * on a Date object is not enough.
 */
export function canBuildDayBounds(
  selectedDate: Date | undefined | null,
  timezone: string | undefined | null,
): boolean {
  if (!timezone || !selectedDate) {
    return false;
  }

  try {
    toDayBoundsUtc(selectedDate, timezone);
    return true;
  } catch {
    return false;
  }
}
